<template>
  <div class="form-container">
    <h2 class="text-responsive-lg font-bold text-center text-gray-800 mb-responsive">{{ title }}</h2>
    
    <!-- Loading state -->
    <div v-if="isLoading" class="text-center py-responsive">
      <svg class="animate-spin h-10 w-10 text-blue-500 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      <p class="text-gray-600 text-responsive-sm">{{ loadingMessage || 'Loading...' }}</p>
    </div>
    
    <!-- Error state -->
    <div v-else-if="errorMessage" class="text-center py-responsive">
      <div class="max-w-md mx-auto">
        <!-- Error Icon -->
        <div class="flex justify-center mb-4">
          <div class="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
            <svg class="h-6 w-6 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
          </div>
        </div>
        
        <!-- Error Title -->
        <h3 class="text-responsive-lg font-medium text-gray-800 mb-2">Submission Failed</h3>
        
        <!-- Error Message -->
        <p class="text-responsive-sm text-gray-600 mb-6">{{ errorMessage }}</p>
        
        <!-- Try Again Button -->
        <button 
          type="button"
          class="btn-responsive-primary bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500 mb-4"
          @click="$emit('retry')"
        >
          Try Again
        </button>
        
        <!-- Secondary Actions -->
        <div class="text-center">
          <p class="text-responsive-xs text-gray-500 mb-2">Still having trouble?</p>
          <div class="space-x-4">
            <button 
              type="button"
              class="text-responsive-xs text-blue-600 hover:text-blue-800 underline"
              @click="$emit('contact-support')"
            >
              Contact Support
            </button>
            <button 
              type="button"
              class="text-responsive-xs text-gray-600 hover:text-gray-800 underline"
              @click="$emit('clear-error')"
            >
              Start Over
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Success state -->
    <div v-else-if="successMessage" class="bg-green-50 border-l-4 border-green-500 p-responsive mb-responsive">
      <div class="flex">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <p class="text-responsive-sm text-green-700">{{ successMessage }}</p>
        </div>
      </div>
    </div>
    
    <!-- Content -->
    <div v-else>
      <slot></slot>
    </div>
    
    <!-- Submit button -->
    <div v-if="!isLoading && !successMessage" class="mt-responsive">
      <button 
        type="button" 
        :disabled="isLoading || isSubmitDisabled"
        class="btn-responsive-primary"
        :class="{
          'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500': !isLoading && !isSubmitDisabled,
          'bg-gray-400 text-gray-700 cursor-not-allowed': isLoading || isSubmitDisabled
        }"
        @click="!isLoading && !isSubmitDisabled ? $emit('submit') : null"
      >
        <div v-if="isLoading" class="flex items-center justify-center">
          <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {{ loadingMessage || 'Loading...' }}
        </div>
        <span v-else>{{ submitLabel || 'Submit' }}</span>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FormContainer',
  
  props: {
    title: {
      type: String,
      required: true
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    loadingMessage: {
      type: String,
      default: 'Loading...'
    },
    errorMessage: {
      type: String,
      default: ''
    },
    successMessage: {
      type: String,
      default: ''
    },
    submitLabel: {
      type: String,
      default: 'Submit'
    },
    isSubmitDisabled: {
      type: Boolean,
      default: false
    }
  },
  
  emits: ['submit', 'retry', 'contact-support', 'clear-error']
};
</script> 